import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { guid } from "@buildwithflux/shared";
import { create } from "mutative";
var PropertySubjectType;
(function (PropertySubjectType) {
    PropertySubjectType["Document"] = "document";
    PropertySubjectType["Elements"] = "elements";
    PropertySubjectType["Nets"] = "nets";
    PropertySubjectType["Routes"] = "routes";
})(PropertySubjectType || (PropertySubjectType = {}));
/**
 * Generates a function that creates a preset property based on the provided default property definition.
 */
export function generatePresetCreationDataFor(defaultPropertyKey) {
    const defaultPropertyDefinition = defaultPropertyDefinitions[defaultPropertyKey];
    const name = defaultPropertyDefinition.label;
    return {
        name,
        create: (overrideData) => ({
            uid: guid(),
            order: 0, // Expected to be overridden by the caller
            name,
            value: "",
            valueType: defaultPropertyDefinition.valueType,
            isPreset: true,
            isSetBySystem: true,
            ...overrideData,
        }),
    };
}
/**
 * Map of property presets for different property subject types.
 */
const propertyPresetsByType = {
    [PropertySubjectType.Document]: [],
    [PropertySubjectType.Elements]: [],
    [PropertySubjectType.Nets]: [
        generatePresetCreationDataFor("net_type"),
        generatePresetCreationDataFor("current"),
        generatePresetCreationDataFor("max_current"),
    ],
    [PropertySubjectType.Routes]: [],
};
/**
 * Mutates the supplied properties map by adding system-default properties (presets) for the given property subject type, if they don't already exist.
 */
function addPresetsToPropertyMap(propertySubjectType, properties) {
    const presets = propertyPresetsByType[propertySubjectType];
    if (presets.length === 0) {
        return; // No presets to add
    }
    // Create lookup for quick access to existing properties by name
    const existingPropertiesByName = new Set(Object.values(properties).map((prop) => prop.name));
    for (const { name, create } of presets) {
        // If the property already exists, skip adding it
        if (existingPropertiesByName.has(name)) {
            continue;
        }
        const presetProperty = create({ order: Object.keys(properties).length });
        properties[presetProperty.uid] = presetProperty;
    }
}
/**
 * Returns a mutated copy of the document data with system-default properties (presets) added the property maps, if they don't already exist.
 */
export function addPresetPropertiesToDocumentData(documentData) {
    return create(documentData, (draftDocumentData) => {
        // Add document-level properties
        addPresetsToPropertyMap(PropertySubjectType.Document, draftDocumentData.properties);
        // Add element-level properties
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(draftDocumentData.elements).forEach((element) => {
            addPresetsToPropertyMap(PropertySubjectType.Elements, element.properties);
        });
        // Add net-level properties
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(draftDocumentData.nets).forEach((net) => {
            addPresetsToPropertyMap(PropertySubjectType.Nets, net.properties);
        });
        // Add route-level properties
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(draftDocumentData.routes).forEach((route) => {
            addPresetsToPropertyMap(PropertySubjectType.Routes, route.properties);
        });
    });
}
//# sourceMappingURL=presets.js.map